import logger from "./logger";
import { isAdmin, isSuperAdmin } from "./authorization";
import { clearAuthToken } from "./tokenStorage";

const JWT_PARTS_COUNT = 3;
const USER_KEY = "auth_user";

type JwtPayload = {
  userId?: number;
  email?: string;
  role?: string;
  full_name?: string;
  restaurant_id?: number;
  exp?: number;
  iat?: number;
  [key: string]: unknown;
};

type CurrentUser = {
  userId?: number;
  id?: number;
  email?: string;
  role?: string;
  full_name?: string;
  restaurant_id?: number;
  exp?: number;
  iat?: number;
  [key: string]: unknown;
};

function normalizeUser(payload: JwtPayload | CurrentUser) {
  return {
    userId: payload.userId ?? (typeof payload.id === "number" ? payload.id : undefined),
    id: payload.id ?? (typeof payload.userId === "number" ? payload.userId : undefined),
    email: payload.email,
    role: payload.role,
    full_name: payload.full_name,
    restaurant_id: payload.restaurant_id,
    exp: payload.exp,
    iat: payload.iat,
  } as CurrentUser;
}

function readStoredUser(): CurrentUser | null {
  try {
    const raw = sessionStorage.getItem(USER_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CurrentUser;
    return normalizeUser(parsed);
  } catch (error) {
    logger.warn("Unable to parse stored auth user", error);
    sessionStorage.removeItem(USER_KEY);
    return null;
  }
}

export function decodeJWT(token: string) {
  try {
    if (!token || typeof token !== "string") return null;

    const parts = token.split(".");
    if (parts.length !== JWT_PARTS_COUNT) return null;

    const payload = parts[1];
    if (!payload) return null;
    const base64Payload = payload.replace(/-/g, "+").replace(/_/g, "/");
    const paddedPayload = base64Payload + "=".repeat((4 - (base64Payload.length % 4)) % 4);
    const decodedPayload = atob(paddedPayload);
    const parsed = JSON.parse(decodedPayload) as JwtPayload;

    if (parsed.exp && parsed.exp < Date.now() / 1000) {
      logger.warn("JWT token has expired");
      return null;
    }

    return parsed;
  } catch (error) {
    logger.error("Error decoding JWT token:", error);
    return null;
  }
}

export function getCurrentUser() {
  const storedUser = readStoredUser();
  if (storedUser) return storedUser;

  return null;
}

export function setCurrentUser(user: CurrentUser | null | undefined) {
  if (!user) {
    sessionStorage.removeItem(USER_KEY);
    return;
  }
  sessionStorage.setItem(USER_KEY, JSON.stringify(normalizeUser(user)));
}

export function isAdminUser() {
  const user = getCurrentUser();
  return Boolean(user?.role && isAdmin(user.role.toLowerCase()));
}

export function isSuperAdminUser() {
  const user = getCurrentUser();
  return Boolean(user?.role && isSuperAdmin(user.role.toLowerCase()));
}

export function isManagerUser() {
  const user = getCurrentUser();
  return Boolean(user && user.role && user.role.toLowerCase() === "manager");
}

export function canAccessAdmin() {
  return isAdminUser() || isManagerUser();
}

export function isStaffUser() {
  const user = getCurrentUser();
  return Boolean(user && user.role && !canAccessAdmin());
}

export function isUserAuthenticated() {
  const user = getCurrentUser();
  return Boolean(user && (isSuperAdminUser() || user.restaurant_id != null));
}

export function isTokenExpired() {
  const user = getCurrentUser();
  if (!user || !user.exp) return true;

  return user.exp < Date.now() / 1000;
}

export function getTokenTimeToExpiry() {
  const user = getCurrentUser();
  if (!user || !user.exp) return 0;

  const now = Date.now() / 1000;
  const timeLeft = (user.exp - now) * 1000;

  return Math.max(0, timeLeft);
}

export function clearToken() {
  setCurrentUser(null);
  clearAuthToken();
}

export function isValidTokenFormat(token: string) {
  if (!token || typeof token !== "string") return false;

  const parts = token.split(".");
  return parts.length === JWT_PARTS_COUNT;
}
